// Admin oturumu: telefon + SMS kodu; yalnız SEROTA platform üyeliği (PLATFORM kiracısı) girebilir.
// Platform yetkileri (platform, firmalar, abonelikler, destek, güvenlik, KVKK, denetim) bağlamdan okunur.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { can, CONTEXT_CHANGED_EVENT, type Action, type MeContext, type PermissionModule } from '@serota/domain';
import { api, ApiError, getCompanyId, onUnauthorized, setCompanyId, tokenStore } from './api';
import { queryClient } from './query';
import { realtime } from './realtime';

type Status = 'loading' | 'signedOut' | 'signedIn';

interface SessionApi {
  status: Status;
  context: MeContext | null;
  signIn: (phone: string, code: string) => Promise<MeContext>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (module: PermissionModule, action?: Action) => boolean;
}

const Ctx = createContext<SessionApi | null>(null);

/**
 * Saklı firma seçimi artık üyelikte değilse (firma silindi, üyelik kaldırıldı, veritabanı yeniden kuruldu) API bağlamı
 * 403 ile reddeder; seçim bırakılıp bir kez daha istenir — yoksa kullanıcı giriş yapsa da panele hiç giremez.
 */
async function freshContext(): Promise<MeContext> {
  try {
    return await api.context();
  } catch (e) {
    if (e instanceof ApiError && e.status === 403 && getCompanyId()) {
      setCompanyId(null);
      return api.context();
    }
    throw e;
  }
}

/** Platform üyeliği olmayan kullanıcı Admin'e giremez. */
export class NotPlatformError extends Error {
  constructor() {
    super(
      'Bu numara SEROTA platform ekibine bağlı değil. Firma paneli için panel adresini (panel.serota.tr) kullanın.',
    );
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [context, setContext] = useState<MeContext | null>(null);

  const load = useCallback(async () => {
    const c = await freshContext();
    const platform = c.memberships.find((m) => m.tenantType === 'PLATFORM');
    if (platform && c.active?.companyId !== platform.companyId) {
      setCompanyId(platform.companyId);
      const again = await api.context();
      setContext(again);
      return again;
    }
    setContext(c);
    return c;
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const t = await tokenStore.get();
      if (!t) return alive && setStatus('signedOut');
      try {
        const c = await load();
        if (!alive) return;
        setStatus(c.active?.tenantType === 'PLATFORM' ? 'signedIn' : 'signedOut');
      } catch {
        if (alive) setStatus('signedOut');
      }
    })();
    const off = onUnauthorized(() => {
      setContext(null);
      setStatus('signedOut');
    });
    return () => {
      alive = false;
      off();
    };
  }, [load]);

  useEffect(() => {
    if (status === 'signedIn') realtime.start();
    else realtime.stop();
  }, [status]);

  // bağlam değişti (bayrak/plan): menü, sekme ve ekranlar aynı anda güncellenir
  useEffect(() => {
    if (status !== 'signedIn') return;
    realtime.subscribe('company');
    const off = realtime.on((e) => {
      if (e.type !== CONTEXT_CHANGED_EVENT) return;
      void load()
        .then(() => queryClient.invalidateQueries())
        .catch(() => undefined);
    });
    return () => {
      off();
      realtime.unsubscribe('company');
    };
  }, [status, load]);

  const signOut = useCallback(async () => {
    await api.logout();
    setCompanyId(null);
    queryClient.clear();
    realtime.stop();
    setContext(null);
    setStatus('signedOut');
  }, []);

  const value = useMemo<SessionApi>(
    () => ({
      status,
      context,
      async signIn(phone, code) {
        await api.verifyOtp(phone, code, 'admin-web');
        const c = await load();
        if (c.active?.tenantType !== 'PLATFORM') {
          await api.logout();
          setCompanyId(null);
          setContext(null);
          throw new NotPlatformError();
        }
        setStatus('signedIn');
        return c;
      },
      signOut,
      async refresh() {
        await load().catch((e) => {
          if (!(e instanceof ApiError)) throw e;
        });
      },
      can: (module, action = 'view') => (context ? can(context.permissions, module, action) : false),
    }),
    [status, context, load, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('SessionProvider eksik');
  return v;
}

export { getCompanyId };
